import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { mockOrder } from '@/mocks/orderMockData';

const SectionDivider = () => (
  <div className="w-full h-px bg-[#C9A84C]/30" />
);

export default function OrderConfirmation() {
  useEffect(() => {
    document.title = 'Order Confirmed | MANDIE OZEE.';
  }, []);

  return (
    <div className="min-h-screen bg-[#FDFAF8]">
      <Navbar />

      <main>
        {/* Success Hero */}
        <section className="relative bg-[#1A1A1A] pt-28 pb-16 md:pt-40 md:pb-28 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute top-[-20%] left-[-10%] w-[500px] h-[500px] rounded-full bg-[#D4006A]/8 blur-[120px]" />
            <div className="absolute bottom-[-30%] right-[-5%] w-[400px] h-[400px] rounded-full bg-[#C9A84C]/8 blur-[100px]" />
          </div>

          <div className="relative z-[1] w-full px-4 md:px-8 lg:px-12">
            <div className="max-w-xl mx-auto text-center">
              {/* Success Icon */}
              <div className="w-20 h-20 md:w-24 md:h-24 mx-auto mb-8 rounded-full bg-[#C9A84C]/15 flex items-center justify-center">
                <div className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-[#C9A84C] flex items-center justify-center">
                  <i className="ri-check-line text-2xl md:text-3xl text-[#1A1A1A]" />
                </div>
              </div>

              {/* Thank You */}
              <p className="text-[#C9A84C] text-xs font-body tracking-[0.25em] uppercase mb-4">
                Thank you for your order
              </p>

              <h1 className="font-heading text-xl md:text-3xl lg:text-5xl text-[#FDFAF8] leading-tight mb-4">
                Your healing companion is on its way.
              </h1>

              <p className="font-body text-sm md:text-base text-[#BBBBBB] mb-2">
                Order <span className="text-[#FDFAF8] font-medium">{mockOrder.orderNumber}</span> has been confirmed
              </p>

              <p className="font-body text-sm text-[#999999]">
                A confirmation email has been sent to <span className="text-[#CCCCCC]">{mockOrder.email}</span>
              </p>
            </div>
          </div>
        </section>

        <SectionDivider />

        {/* Order Details */}
        <section className="py-14 md:py-20">
          <div className="w-full px-4 md:px-8 lg:px-12">
            <div className="max-w-3xl mx-auto">
              {/* Status Bar */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-10 pb-8 border-b border-[#C9A84C]/20">
                <div>
                  <p className="font-heading text-xl md:text-2xl text-[#1A1A1A] mb-1">
                    Order Details
                  </p>
                  <p className="font-body text-xs text-[#999999]">
                    Placed on {mockOrder.orderDate}
                  </p>
                </div>
                <span className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#C9A84C]/10 text-[#C9A84C] text-xs font-body font-medium rounded-full">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#C9A84C]" />
                  {mockOrder.status}
                </span>
              </div>

              {/* Order Items */}
              <div className="mb-10">
                {mockOrder.items.map((item, idx) => (
                  <div
                    key={idx}
                    className="flex flex-col sm:flex-row gap-5 p-5 md:p-6 bg-[#FDFAF8] border border-[#C9A84C]/15 rounded-lg"
                  >
                    <div className="w-full sm:w-28 h-28 flex-shrink-0 bg-[#F5F0EB] rounded-md overflow-hidden">
                      <img
                        src={item.image}
                        alt={item.name}
                        className="w-full h-full object-cover object-center"
                        title={item.name}
                      />
                    </div>
                    <div className="flex-1 flex flex-col justify-center">
                      <h3 className="font-heading text-lg text-[#1A1A1A] mb-0.5">
                        {item.name}
                      </h3>
                      <p className="font-body text-xs text-[#888888] mb-1">
                        {item.description}
                      </p>
                      <p className="font-body text-xs text-[#999999]">
                        Qty: {item.quantity}
                      </p>
                    </div>
                    <div className="flex sm:flex-col items-center sm:items-end justify-between sm:justify-center">
                      <p className="font-heading text-lg text-[#1A1A1A]">
                        {item.price.toLocaleString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Price Breakdown */}
              <div className="mb-10 pb-8 border-b border-[#C9A84C]/20">
                <div className="space-y-3">
                  <div className="flex justify-between font-body text-sm text-[#666666]">
                    <span>Subtotal</span>
                    <span>{mockOrder.subtotal.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between font-body text-sm text-[#666666]">
                    <span>Shipping</span>
                    <span className="text-[#C9A84C]">Free</span>
                  </div>
                  <div className="flex justify-between font-body text-sm text-[#666666]">
                    <span>Tax</span>
                    <span>{mockOrder.tax.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between font-heading text-lg text-[#1A1A1A] pt-3 border-t border-[#C9A84C]/20">
                    <span>Total</span>
                    <span>{mockOrder.total.toLocaleString()}</span>
                  </div>
                </div>
              </div>

              {/* Info Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
                {/* Shipping Address */}
                <div className="p-5 md:p-6 bg-[#FDFAF8] border border-[#C9A84C]/15 rounded-lg">
                  <div className="flex items-center gap-2 mb-3">
                    <i className="ri-map-pin-line text-[#C9A84C] text-lg" />
                    <p className="font-heading text-sm text-[#1A1A1A]">Shipping Address</p>
                  </div>
                  <div className="font-body text-sm text-[#666666] space-y-0.5">
                    <p className="text-[#1A1A1A] font-medium">{mockOrder.shippingAddress.name}</p>
                    <p>{mockOrder.shippingAddress.line1}</p>
                    <p>{mockOrder.shippingAddress.city}, {mockOrder.shippingAddress.state} {mockOrder.shippingAddress.zip}</p>
                    <p>{mockOrder.shippingAddress.country}</p>
                  </div>
                </div>

                {/* Payment + Delivery */}
                <div className="p-5 md:p-6 bg-[#FDFAF8] border border-[#C9A84C]/15 rounded-lg">
                  <div className="mb-5">
                    <div className="flex items-center gap-2 mb-3">
                      <i className="ri-bank-card-line text-[#C9A84C] text-lg" />
                      <p className="font-heading text-sm text-[#1A1A1A]">Payment</p>
                    </div>
                    <div className="flex items-center gap-2 font-body text-sm text-[#666666]">
                      <span className="text-[#1A1A1A] font-medium">{mockOrder.paymentMethod.type}</span>
                      <span>ending in {mockOrder.paymentMethod.last4}</span>
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <i className="ri-truck-line text-[#C9A84C] text-lg" />
                      <p className="font-heading text-sm text-[#1A1A1A]">Estimated Delivery</p>
                    </div>
                    <p className="font-body text-sm text-[#666666]">{mockOrder.estimatedDelivery}</p>
                  </div>
                </div>
              </div>

              {/* Gift Message */}
              {mockOrder.giftMessage && (
                <div className="p-5 md:p-6 bg-[#FDFAF8] border border-[#C9A84C]/15 rounded-lg mb-10">
                  <div className="flex items-center gap-2 mb-3">
                    <i className="ri-quill-pen-line text-[#C9A84C] text-lg" />
                    <p className="font-heading text-sm text-[#1A1A1A]">Gift Message</p>
                  </div>
                  <p className="font-quote text-sm md:text-base italic text-[#666666]">
                    {mockOrder.giftMessage}
                  </p>
                </div>
              )}
            </div>
          </div>
        </section>

        <SectionDivider />

        {/* What's Next */}
        <section className="py-14 md:py-20">
          <div className="w-full px-4 md:px-8 lg:px-12">
            <div className="max-w-3xl mx-auto text-center">
              <p className="text-[#C9A84C] text-xs font-body tracking-[0.25em] uppercase mb-4">
                What's next
              </p>

              <h2 className="font-heading text-xl md:text-2xl text-[#1A1A1A] mb-12 leading-tight">
                While you wait, a few things to look forward to.
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
                {/* Step 1 */}
                <div className="text-center">
                  <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-[#C9A84C]/10 flex items-center justify-center">
                    <i className="ri-mail-check-line text-xl text-[#C9A84C]" />
                  </div>
                  <p className="font-heading text-sm text-[#1A1A1A] mb-2">Confirmation Email</p>
                  <p className="font-body text-xs text-[#999999]">
                    You'll receive your order confirmation and receipt within minutes.
                  </p>
                </div>

                {/* Step 2 */}
                <div className="text-center">
                  <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-[#C9A84C]/10 flex items-center justify-center">
                    <i className="ri-shopping-bag-3-line text-xl text-[#C9A84C]" />
                  </div>
                  <p className="font-heading text-sm text-[#1A1A1A] mb-2">Processing & Packaging</p>
                  <p className="font-body text-xs text-[#999999]">
                    Your set is carefully hand-packed and prepared for shipment within 2–3 business days.
                  </p>
                </div>

                {/* Step 3 */}
                <div className="text-center">
                  <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-[#C9A84C]/10 flex items-center justify-center">
                    <i className="ri-truck-line text-xl text-[#C9A84C]" />
                  </div>
                  <p className="font-heading text-sm text-[#1A1A1A] mb-2">Tracking Info</p>
                  <p className="font-body text-xs text-[#999999]">
                    Once shipped, you'll get an email with tracking details so you know exactly when it arrives.
                  </p>
                </div>
              </div>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link
                  to="/shop"
                  className="inline-flex items-center justify-center px-8 py-4 bg-[#D4006A] text-white text-sm font-body font-medium rounded-full hover:bg-[#B3005A] transition-colors duration-300 whitespace-nowrap"
                >
                  Continue Shopping
                </Link>
                <Link
                  to="/"
                  className="inline-flex items-center justify-center px-8 py-4 border border-[#C9A84C]/40 text-[#1A1A1A] text-sm font-body font-medium rounded-full hover:border-[#C9A84C] hover:bg-[#C9A84C]/5 transition-colors duration-300 whitespace-nowrap"
                >
                  Back to Home
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}